'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics from Car Tires: The Biggest Ocean Source You've Never Heard Of"
      author="MicroPlastics Editorial Team"
      date="June 25, 2025"
      readTime="10 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics from car tires, the biggest ocean source"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Car tires are the <strong>single largest source of ocean microplastics
        worldwide</strong>, accounting for an estimated <strong>78% of all
        microplastic released into the oceans</strong> per the Pew Charitable
        Trust analysis. A single car releases approximately <strong>1 trillion
        ultrafine particles per kilometre driven</strong>, with particles small
        enough to pass directly through the lungs into the bloodstream. Tire
        wear contributes ~3.6 million tonnes per year globally. The exposure is
        primarily inhalation and waterway pollution; consumer reduction is
        limited but real (driving less, EV with regenerative braking,
        appropriate tire pressure, less aggressive driving).
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Tire wear releases an estimated <strong>3.6 million tonnes of microplastic per year</strong> globally, making it the single largest source.</>,
          <>78% of ocean microplastic is from synthetic tire rubber (Pew Charitable Trust analysis).</>,
          <>A single car releases ~1 trillion ultrafine particles per km driven; particles &lt;100 nm pass directly into the bloodstream.</>,
          <>Wind-borne tire microplastic reaches the ocean faster than river runoff, direct atmospheric transport.</>,
          <>Consumer options are limited but real: drive less, maintain tire pressure, gentle driving, EV with regenerative braking.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why tires are the biggest microplastic source
      </h2>
      <p>
        Modern tires are <strong>50-60% synthetic rubber</strong>
        (styrene-butadiene rubber and butadiene rubber) blended with natural
        rubber, carbon black, silica, oils, and various additives. Every time
        a tire grips the road, it sheds tiny fragments, a process accelerated
        by braking, cornering, acceleration, and rough road surfaces.
      </p>
      <p>
        The scale is staggering. Estimates from multiple research groups:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>3.6 million tonnes per year</strong> of tire microplastic globally (Wagner et al. 2018).</li>
        <li><strong>1 trillion ultrafine particles per km</strong> released by a single passenger car (multiple recent studies).</li>
        <li><strong>78% of ocean microplastic</strong> is from synthetic tire rubber (Pew Charitable Trust 2020 analysis).</li>
        <li><strong>200,000 tonnes per year</strong> of tire particles wind-blown from roads to oceans (2020 Nature Communications study).</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How tire particles reach your body
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Inhalation in urban traffic.</strong> Tire microplastic
          concentrates within ~10 m of roads. Pedestrians, cyclists, and
          residents near major roads inhale measurably more.
        </li>
        <li>
          <strong>Indoor air infiltration.</strong> Tire wear is a major
          contributor to urban PM2.5 air pollution, which enters buildings
          through ventilation.
        </li>
        <li>
          <strong>Drinking water.</strong> Tire wear runoff is a recognised
          contributor to drinking-water microplastic loads in dense urban areas.
        </li>
        <li>
          <strong>Coastal seafood.</strong> Tire-related microplastic is among
          the dominant polymer types found in ocean wildlife and seafood.
        </li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Driving behaviours that increase tire microplastic
      </h2>

      <BlogComparisonTable
        caption="Driving factors that affect tire microplastic release"
        columns={[
          { key: 'factor', label: 'Factor' },
          { key: 'impact', label: 'Impact on release' },
        ]}
        rows={[
          { factor: 'Aggressive acceleration / hard braking', impact: 'Major increase, friction force scales with particle generation' },
          { factor: 'Under-inflated tires', impact: '20%+ more wear per km' },
          { factor: 'Heavy vehicles (SUVs, trucks, EVs)', impact: 'Linear increase with weight, heavier = more tire wear' },
          { factor: 'High-grip "performance" tires', impact: 'Higher wear rate than touring tires' },
          { factor: 'Rough road surfaces', impact: 'Higher abrasion than smooth pavement' },
          { factor: 'Highway cruising (steady speed)', impact: 'Lowest per-km release' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Are EVs better or worse for tire microplastic?
      </h2>
      <p>
        It depends. Electric vehicles are heavier than equivalent
        gasoline cars (typically 200-700 kg more for the battery), which
        increases per-km tire wear. <em>However</em>, EVs use
        <strong> regenerative braking</strong> for most stops, which uses the
        motor rather than friction brakes, reducing brake dust microplastic
        substantially. Net effect: depends on driving style and battery size.
        Gentle driving in a smaller EV often produces lower total particle
        emissions than aggressive driving in a heavy gas car.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What you can actually do
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Drive less.</strong> Walk, bike, or use transit when possible. Reduces both tire wear and exposure.</li>
        <li><strong>Live further from major roads</strong> if you have a choice. Within 10 m of a busy road, indoor air is measurably worse.</li>
        <li><strong>Maintain tire pressure</strong> at manufacturer spec. Underinflated tires wear 20%+ faster.</li>
        <li><strong>Drive gently.</strong> Steady acceleration and braking reduce per-km wear noticeably.</li>
        <li><strong>Use a HEPA-equipped vacuum</strong> for car interiors, tire and brake dust accumulates fast.</li>
        <li><strong>Run a HEPA air purifier in your bedroom</strong> if you live on a busy road.</li>
        <li><strong>Choose touring tires over performance</strong> when buying new (touring tires last 20-30% longer).</li>
        <li><strong>Consider an EV for daily driving</strong>, especially smaller models, regenerative braking reduces brake dust.</li>
      </ol>

      <p>
        See related:{' '}
        <Link href="/blog/airborne-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          airborne microplastics
        </Link>,{' '}
        <Link href="/blog/microplastics-in-the-air-at-home" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in the air at home
        </Link>, and{' '}
        <Link href="/blog/microplastics-in-tap-water" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in tap water
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Product packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from photo, scratches, dents, fade.</>,
          <>Brand and product category, different SKUs in the same brand can score differently.</>,
          <>Use-context flags you log, microwave, heat, reuse.</>,
          <>Cited published research behind each 0–100 score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Get the MicroPlastics app"
        sub="Scan barcodes + packaging photos. Get a 0–100 microplastic risk score with linked research and safer alternatives. Free to start on iOS."
        buttonLabel="Get the MicroPlastics app"
      />

      <BlogFAQ
        items={[
          {
            question: 'What percentage of ocean microplastics come from car tires?',
            answer:
              'An estimated 78% of ocean microplastics come from synthetic tire rubber, per the Pew Charitable Trust 2020 analysis. Tire wear is the single largest source of microplastic worldwide, ahead of synthetic textiles, marine paint, and personal-care products.',
          },
          {
            question: 'How much microplastic does a single car release?',
            answer:
              'A single passenger car releases approximately 1 trillion ultrafine particles per kilometre driven. Globally, tire wear contributes ~3.6 million tonnes of microplastic per year (Wagner et al. 2018).',
          },
          {
            question: 'Are electric vehicles worse for tire microplastics?',
            answer:
              'EVs are heavier than equivalent gas cars (200-700 kg more for batteries), increasing per-km tire wear. However, regenerative braking reduces brake-dust microplastic substantially. Net effect depends on driving style and EV size.',
          },
          {
            question: 'Do tire microplastics affect human health?',
            answer:
              'Yes. Tire particles concentrate within 10 m of roads, where pedestrians, cyclists, and residents inhale measurably more. Ultrafine tire particles (<100 nm) are small enough to pass through the lungs into the bloodstream. Tire-derived microplastic is a major contributor to urban PM2.5 pollution.',
          },
          {
            question: 'Can I reduce my exposure to tire microplastics?',
            answer:
              'Yes, modestly. Drive less, maintain tire pressure, drive gently, live further from major roads, use a HEPA bedroom air purifier in urban areas, and choose touring (long-life) over performance tires. The biggest reductions come from collective infrastructure decisions, not individual ones.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Wagner S, Hüffer T, Klöckner P, et al.',
            year: 2018,
            title: 'Tire wear particles in the aquatic environment - A review on generation, analysis, occurrence, fate and effects',
            publisher: 'Water Research',
            url: 'https://www.sciencedirect.com/science/article/abs/pii/S0043135418303749',
          },
          {
            authors: 'Pew Charitable Trusts & SYSTEMIQ',
            year: 2020,
            title: 'Breaking the Plastic Wave: A comprehensive assessment of pathways towards stopping ocean plastic pollution',
            publisher: 'Pew Charitable Trusts',
            url: 'https://www.pewtrusts.org/en/research-and-analysis/articles/2020/07/23/breaking-the-plastic-wave-top-findings',
          },
          {
            authors: 'Evangeliou N, Grythe H, Klimont Z, et al.',
            year: 2020,
            title: 'Atmospheric transport is a major pathway of microplastics to remote regions',
            publisher: 'Nature Communications',
            url: 'https://www.nature.com/articles/s41467-020-17201-9',
          },
          {
            authors: 'Booth AM, Sørensen L',
            year: 2024,
            title: 'Vehicle tyres – A key source of microplastics to the environment',
            publisher: 'IKHAPP report',
            url: 'https://ikhapp.org/wp-content/uploads/2024/09/Booth-et-al.-2024-Vehicle-tyres-%E2%80%93-A-key-source-of-microplastics-to-the-environment.-An-introduction-to-fate-effects-and-mitigation-strategies.pdf',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
